import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A failed 3D scene must never take the page with it.
 *
 * WebGL fails in ways ordinary React code does not: a lost context when the OS
 * reclaims the GPU, a driver that rejects a shader, an out-of-memory on a tab
 * that has been open for hours. Any of those throws during render or inside
 * `useFrame`, and without a boundary the whole marketing page unmounts — a blank
 * screen where the headline and the pricing used to be.
 *
 * So the decorative layer is isolated: if it dies, the fallback takes its place
 * and every word on the page survives.
 */
export class SceneErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Left in production deliberately. A hero that silently degrades on a class
    // of GPU is a bug nobody reports, because the page still "works".
    console.warn('[hero] 3D scene failed, using the static fallback', error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
